import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Grid, Typography } from 'gu-ui-kit'
import Breadcrumbs from 'components/Breadcrumbs'
import FilterPanel from 'components/Filters/FilterPanel'
import useFiltersManager from 'utils/hooks/useFiltersManager'
import config from 'modules/AccountsMarketplace/Products/filters'
import { DROP_DATA, FETCH_PRODUCTS, SET_FILTERS } from 'modules/AccountsMarketplace/Products/types'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import { useTranslation } from 'react-i18next'
import Table from 'modules/AccountsMarketplace/Products/subcomponents/Table'
import { Helmet } from 'react-helmet'
import { ROUTES } from 'constants/routes'
import { useGetCategory } from 'modules/AccountsMarketplace/hooks/useGetCategory'

const AccountsMarketplaceProducts = () => {
  const { categoryId } = useParams()
  const { t } = useTranslation()
  const pathGen = useGeneratePathI18n()
  const dispatch = useDispatch()
  const [page, setPage] = useState(1)
  const { category } = useGetCategory(categoryId)

  const { filters, products, pagination, loading } = useSelector(
    (state) => state['accounts-marketplace/products'],
  )

  const filterManager = useFiltersManager({
    filters: filters,
    config: config(),
    setAction: SET_FILTERS,
    localStorageKey: 'accounts-marketplace/products',
  })

  useEffect(() => {
    filterManager.params &&
      dispatch({
        type: FETCH_PRODUCTS,
        payload: {
          params: {
            category_ids: [categoryId],
            ...filterManager.params,
            offset: (page - 1) * pagination.limit,
            limit: pagination.limit,
          },
        },
      })
  }, [filterManager.params, page])

  useEffect(() => {
    return () => {
      dispatch({
        type: DROP_DATA,
      })
    }
  }, [])

  useEffect(() => {
    dispatch({ type: DROP_DATA })
    setPage(1)
  }, [filterManager.filters])

  const handleChangePage = useCallback(() => setPage(page + 1), [setPage, page])

  const breadCrumbsConfig = useMemo(
    () => [
      {
        title: t('accounts_marketplace::title'),
        to: pathGen(ROUTES.ACCOUNTS_MARKETPLACE),
      },
      {
        title: category?.name || '',
        to: pathGen(ROUTES.ACCOUNTS_MARKETPLACE_CATEGORY, {
          categoryId,
        }),
      },
      {
        title: t('accounts_marketplace::breadcrumbs.buy_account'),
      },
    ],
    [categoryId, category, t],
  )

  return (
    <>
      <Helmet defer={false}>
        <title>
          {`${t('accounts_marketplace::navigation.products')} — ${t(
            'accounts_marketplace::title',
          )}`}
        </title>
      </Helmet>
      <Grid gap={24} direction={'column'} className={'margin-bottom-64'}>
        <Breadcrumbs links={breadCrumbsConfig} className={'margin-bottom-0'} />

        <Typography component={'h1'} variant={'Header-60'} co>
          {category?.name}
        </Typography>
      </Grid>

      <FilterPanel filterManager={filterManager} className={'margin-bottom-32'} />

      <Table
        categoryId={categoryId}
        onChangePage={handleChangePage}
        data={products}
        pagination={pagination}
        loading={loading}
      />
    </>
  )
}

export default AccountsMarketplaceProducts
